import React, { useCallback, useEffect, useMemo, useState } from "react";

import S3Upload from "react-s3-uploader/s3upload";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Dropzone, { DropzoneRef } from "react-dropzone";
import { ProgressBar } from "react-bootstrap";

import { nanoid } from "nanoid";
import dateformat from "dateformat";
import moment from "moment";

import InfiniteScroll from "src/components/infiniteScroll";
import Slider from "src/components/slider";
import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";

import { FileService } from "src/utils/file.service";
import AuthService from "src/utils/auth.service";
import { AssetFile } from "src/types/assets";
import FileUploadService from "src/utils/file.upload.service";
import { UploadedCallItem } from "src/redux/reducers/coaching/callLibraryReducer";

import { fetchCallMeetings, uploadCallRecording, deleteUploadCallRecording, invalidateUploadCallRecording, fetchUploadCallMeetings } from "src/redux/actions/coaching/callLibrary";
import { uploadAsset, uploadAssetFailed, uploadAssetProgress, uploadingAssetSucceeded } from "src/redux/actions/assets";
import { CallMeeting } from "src/types/coaching/callLibrary";
import { CallRecording } from "src/types/coaching/callRecording";

import _ from "lodash";
import cx from "classnames";
import styles from "./callLibrary.module.scss";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/2.0`;

export const CallLibraryPage = () => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [openSourceSlider, setOpenSourceSlider] = useState(false);

  const {
    loading,
    loaded,
    meetings,
    totalRecordsCount,
    currentPage,
    nextPage,
    hasMoreItems,
  } = useSelector((state: any) => {
    const callMeetingState = _.get(state.coaching, "callMeetings", {});
    const loading = _.get(callMeetingState, "loading");
    const loaded = _.get(callMeetingState, "loaded");
    const pagination = _.get(callMeetingState, "pagination");
    const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const meetings = _.get(callMeetingState, "items") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      loaded: loaded,
      meetings: meetings,
      totalRecordsCount: totalRecordsCount,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
    };
  });

  const getDateLabel = (date: Date) => {
    const now = new Date();

    const toMidnight = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const diffDays = Math.round(
      (toMidnight(now).getTime() - toMidnight(date).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return "This Week";
    if (diffDays < 14) return "Last Week";

    // Older than 2 weeks → show "Month Year" e.g. "January 2025"
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  const groupMeetingsByDate = (meetings: CallMeeting[]) => {
    const groupMap = new Map<string, CallMeeting[]>();

    for (const meeting of meetings) {
      const label = getDateLabel(new Date(meeting?.modifiedDate ?? ""));
      if (!groupMap.has(label)) groupMap.set(label, []);
      groupMap.get(label)!.push(meeting);
    }

    return Array.from(groupMap.entries()).map(([label, data]) => ({
      label,
      data,
    }));
  };

  const callMeetings = useMemo(() => {
    return groupMeetingsByDate(meetings);
  }, [meetings]);

  const handleLoadMore = () => {
    if (nextPage) {
      const request = {
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCallMeetings(request));
    }
  };

  useEffect(() => {
    if (callMeetings.length > 0) {
      return;
    }
    dispatch(
      fetchCallMeetings({
        page: 0,
        limit: 10,
      }),
    );
  }, []);

  if (!loaded && loading && meetings.length === 0) {
    return (
      <div className={styles.screen}>
        <div className={styles.container}>
          <div className={styles.list}>
            <CallRecordingCardPlaceholder />
            <CallRecordingCardPlaceholder />
            <CallRecordingCardPlaceholder />
          </div>
        </div>
      </div>
    );
  }
  
  if (callMeetings.length === 0 && !loading && loaded) {
    return <>
        <NoCalls onUploadCallsClick={() => setOpenSourceSlider(true)} />
        <div className={styles.noCallsWrapper}>
          <NoCallsPlaceholder />
          <NoCallsPlaceholder />
          <NoCallsPlaceholder />
        </div>
        <AddSourceSlider
          open={openSourceSlider}
          onClose={() => setOpenSourceSlider(false)}
        />
        </>
  }

  return (
    <div className={styles.screen}>
      <div className={styles.container}>
        {/* <div className={styles.searchWrapper}>
          <div className={styles.searchBar}>
            <i className={cx("paperflite-search", styles.searchIcon)} />
            <input
              type="text"
              placeholder="Ask anything ..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={styles.searchInput}
            />
            {query && (
              <button
                className={styles.clearButton}
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                <i className="paperflite-close" />
              </button>
            )}
          </div>
        </div> */}

        <InfiniteScroll
          className={styles.list}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMore}
          loader={() => {
            return _.times(
              _.size(meetings) === 0 || currentPage === 0 ? 3 : 1,
              (index) => <CallRecordingCardPlaceholder key={index} />,
            );
          }}
        >
          {callMeetings.map((group) => (
            <div className={styles.section}>
              <span className={styles.label}>{group.label}</span>
              {group.data.map((item) => (
                <CallCard key={item.id} meeting={item} />
              ))}
            </div>
          ))}
        </InfiniteScroll>
      </div>
      <div className={styles.uploadWrapper}>
        <button
          className={styles.uploadButton}
          onClick={() => setOpenSourceSlider(true)}
        >
          <i className="paperflite-upload" />
          upload calls
        </button>
      </div>
      <AddSourceSlider
        open={openSourceSlider}
        onClose={() => setOpenSourceSlider(false)}
      />
    </div>
  );
};

const CallCard = ({ meeting }: { meeting: CallMeeting }) => {
  const navigate = useNavigate();

  const title = useMemo(() => {
    return meeting.title || "";
  }, [meeting]);

  const summary = useMemo(() => {
    const salesRepPositives = _.get(meeting, "summary.salesRepPositives", []);
    const summary = salesRepPositives.join(" ");

    return summary || "";
  }, [meeting]);

  const duration = useMemo(() => {
    return meeting.duration ?? "";
  }, [meeting]);

  const startTime = useMemo(() =>{
    return meeting.startTime ?? "";
  }, [meeting])

  const prospect = useMemo(() => {
    return meeting.prospect || "";
  }, [meeting]);

  const salesRep = useMemo(() => {
    return meeting.salesRep || "";
  }, [meeting]);

  const modifiedDate = useMemo(() => {
    return meeting.modifiedDate;
  }, [meeting]);

  const score = useMemo(() => {
    return meeting.score || 0;
  }, [meeting]);

  const percentage = Math.floor((score as number) / 5 * 100).toFixed(0) || 0
  const uploadedBy = useMemo(() => {
    return meeting.uploadedBy || "";
  }, [meeting]);

  const dateMap = new Date(modifiedDate ?? 0);
  const day = dateMap.getDate();
  const month = dateMap.toLocaleString("default", { month: "short" });

  const getScoreLevel = (score) => {
    if (score >= 75) return "high";
    if (score >= 50) return "medium";
    return "low";
  };

  const level = getScoreLevel(score);

  const Avatar = ({ name, small = false }) => {
    const initials = (name = "") => {
      return name.slice(0, 2).toUpperCase();
    };
    return (
      <span className={cx(styles.avatar, { [styles.avatarSmall]: small })}>
        {initials(name)}
      </span>
    );
  };

  return (
    <div
      className={styles.card}
      onClick={() => navigate(`/coaching/meetings/${meeting.id}`)}
    >
      <div className={styles.header}>
        <div className={styles.dateBadge}>
          <span className={styles.month}>{month}</span>
          <span className={styles.day}>{day}</span>
        </div>

        <div className={styles.row}>
          <span className={styles.title}>{title}</span>
          <div className={styles.metadatas}>
            {prospect && (
              <div className={styles.item}>
                <Avatar name={prospect} />
                <span className={styles.name}>Prospect: {prospect}</span>
              </div>
            )}

            {uploadedBy && (
              <div className={styles.item}>
                <Avatar name={uploadedBy} />
                <span className={styles.name}>Uploaded by: {uploadedBy}</span>
              </div>
            )}

            {salesRep && (
            <div className={styles.item}>
              <Avatar name={salesRep} small />
              <span className={styles.name}>Sales Rep: {salesRep}</span>
            </div>
            )}
            <div className={styles.item}>
              <i className={cx("paperflite-clock", styles.clock)} />
              <span className={styles.name}>
                {moment(startTime).isValid()
                  ? moment(startTime).format("hh:mm a")
                  : "N/A"}
              </span>
            </div>
            <span
              className={cx(styles.scorePill, {
                [styles.scorePillHigh]: level === "high",
                [styles.scorePillMedium]: level === "medium",
                [styles.scorePillLow]: level === "low",
              })}
            >
              score : {percentage}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CallRecordingCardPlaceholder = () => {
  return (
    <div className={cx(styles.cardContainer, styles.placeholder)}>
      <div className={styles.topSection}>
        <div className={cx(styles.dateBlock, styles.block)} />
        <div className={styles.cardBody}>
          <div className={cx(styles.titleBar, styles.block)} />
          <div className={styles.metaRow}>
            <div className={cx(styles.metaDetails, styles.block)} />
            <div className={cx(styles.metaDetails, styles.block)} />
            <div className={cx(styles.metadata, styles.block)} />
            <div className={cx(styles.metadata, styles.block)} />
          </div>
        </div>
      </div>
      <div className={cx(styles.bodyBlock, styles.block)} />
    </div>
  );
};

export const NoCallsPlaceholder = () => {
  return (
    <div className={cx(styles.noCalls)}>
      <div className={styles.topSection}>
        <div className={cx(styles.dateBlock)} />
        <div className={styles.cardBody}>
          <div className={cx(styles.titleBar)} />
          <div className={styles.metaRow}>
            <div className={cx(styles.metaDetails)} />
            <div className={cx(styles.metaDetails)} />
            <div className={cx(styles.metadata)} />
            <div className={cx(styles.metadata)} />
          </div>
        </div>
      </div>
      <div className={cx(styles.bodyBlock)} />
    </div>
  );
}

const NoCalls = ({ onUploadCallsClick }: { onUploadCallsClick: () => void }) => {
  return (
    <div className={styles.callContainer}>
      <h2 className={styles.title}>no calls added yet!</h2>
      <p className={styles.subtext}>
        Start by uploading call recordings and see the magic that follows…
      </p>
      <div className={styles.uploadCall}>
      <button
          className={styles.upload}
          onClick={onUploadCallsClick}
        >
          <i className="paperflite-upload" />
          upload calls
        </button>
      </div>
    </div>
  );
};

interface AddSourceSliderProps {
  open: boolean;
  onClose: () => void;    
}

interface DeleteState {
  show: boolean;
  file: CallRecording | null;
  isClosing: boolean;
}

const AddSourceSlider: React.FC<AddSourceSliderProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const dropZoneRef = React.useRef<DropzoneRef>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<any | null>(null);
  const [failed, setFailed] = useState(false);
  const [deleteState, setDeleteState] = useState<DeleteState>({
    show: false,
    file: null,
    isClosing: false,
  });

  const {
    uploadedItemsLoading,
    uploadedItemsLoaded,
    uploadedItems,
    currentPage,
    nextPage,
    hasMoreItems,
    uploadInFlight,
  } = useSelector((state: any) => {
    const uploadedItemsState = _.get(state.coaching, "callMeetings.uploadedItems", {});
    const loading = _.get(uploadedItemsState, "loading", false);
    const loaded = _.get(uploadedItemsState, "loaded", false);
    const pagination = _.get(uploadedItemsState, "pagination");
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems", false);
    const items = _.get(uploadedItemsState, "items", []);

    const nextPageValue = currentPage + 1;
    const nextPage: number | null = nextPageValue >= total ? null : nextPageValue;

    return {
      uploadedItemsLoading: loading,
      uploadedItemsLoaded: loaded,
      uploadedItems: items,
      currentPage,
      nextPage,
      hasMoreItems,
      uploadInFlight: _.get(state.coaching, "callMeetings.upload", {}),
    };
  }, _.isEqual);

  const isUploading = _.values(uploadInFlight).some((u: any) => u.uploading);

  const isEmpty = uploadedItemsLoaded && !uploading && !failed && !isUploading && _.isEmpty(uploadedItems);

  const hasEvaluationCompleted = uploadedItems.some(
    (item: any) => item.evaluationProcessing?.status === "completed"
  );

  const handleBrowseClick = () => {
    dropZoneRef.current?.open();
  };

  const handleFileUploadFinish = (file, data, signResult: any) => {
    setUploading(false);
    setUploadProgress(100);
    FileUploadService.markFinish(data.id);
    dispatch(uploadingAssetSucceeded({ id: data.id }));
    dispatch(uploadCallRecording({
      signedUrl: file.signedUrl,
      id: data?.id,
    }));
  };

  const handleFileUploadError = (file, error: any) => {
    dispatch(uploadAssetFailed({ id: file.id, error }));
    setUploading(false);
    setFailed(true);
  };

  const handleFileUploadProgress = (progress: number, file) => {
    dispatch(uploadAssetProgress({ id: file.id, progress }));
    setUploadProgress(progress);
  };

  const uploadFile = (file) => {
    file.id = nanoid();
    const fileName = _.get(file, "name", "Untitled");
    let fileType = FileService.getFileExtension(fileName);
    if (file.type == "" || file.type == null || file.type == undefined) {
      fileType = "application/octet-stream";
      file = new File([file], fileName, { type: fileType });
      file.id = nanoid();
    }

    const s3Uploader = new S3Upload({
      signingUrl: "/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${AuthService.getAccessToken()}`,
      },
      server: apiUrl,
      scrubFilename: (filename) => {
        const name = filename.replace(/[^\w\d_\-.\s]+/gi, "");
        return encodeURIComponent(name);
      },
      signingUrlQueryParams: { originalName: encodeURIComponent(fileName) },
      contentDisposition: "auto",
      uploadRequestHeaders: {},
      onFinishS3Put: handleFileUploadFinish,
      onProgress: handleFileUploadProgress,
      onError: handleFileUploadError,
      files: [file],
    });

    FileUploadService.trackUpload(s3Uploader);

    dispatch(uploadAsset({
      file: {
        id: file.id,
        name: fileName,
        type: fileType,
        size: file.size,
      } as AssetFile,
    }));
  };

  const handleDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      setUploading(true);
      setFailed(false);
      uploadFile(file);
    }
  };

  const handleLoadMore = () => {
    if (nextPage !== null) {
      dispatch(fetchUploadCallMeetings({ page: nextPage, limit: 10 }));
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteState.file?.id) {
      dispatch(deleteUploadCallRecording({ id: deleteState.file.id }));
      setDeleteState({ show: false, file: null, isClosing: false });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteState((prev) => ({ ...prev, isClosing: true }));
    setTimeout(() => {
      setDeleteState({ show: false, file: null, isClosing: false });
    }, 300);
  };

  const handleDelete = (item: any) => {
    setDeleteState({
      show: true,
      file: { id: item.recordingId, name: item.response?.title },
      isClosing: false,
    });
  };

const handleClose = useCallback(() => {
  dispatch(invalidateUploadCallRecording());
  if (hasEvaluationCompleted) {
    dispatch(fetchCallMeetings({ page: 0, limit: 10 }));
  }
  onClose();
}, [hasEvaluationCompleted]);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchUploadCallMeetings({ page: 0, limit: 10 }));
  }, [open]);

  return (
    <Slider.Dialog
      className={styles.addSourceContainer}
      width="600px"
      show={open}
      onClose={handleClose}
    >
      <Slider.Header>
        <Dropzone
          ref={dropZoneRef}
          noClick
          multiple={false}
          onDrop={handleDrop}
          accept={{
            "video/mp4": [".mp4"],
            "audio/mpeg": [".mp3"],
            "audio/wav": [".wav"],
          }}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()} className={styles.headerContainer}>
              <input {...getInputProps()} type="file" />
              <div className={styles.header}>
                <span className={styles.title}>call library</span>
                <i className="paperflite-close" onClick={handleClose} />
              </div>
              <div className={styles.upload}>
                <div className={styles.details}>
                  <div className={styles.title}>
                    <i className="paperflite-info" />
                    <span className={styles.info}>supported formats</span>
                  </div>
                  <span className={styles.message}>
                    You can upload MP3 or MP4 call recordings.
                  </span>
                </div>
                <div className={styles.actions}>
                  <button
                    className={styles.uploadButton}
                    onClick={handleBrowseClick}
                  >
                    <i className="paperflite-upload"></i>
                    <span className={styles.text}>Upload a new call</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </Dropzone>
      </Slider.Header>

      <Slider.Body className={styles.body}>
        <div className={styles.title}>uploaded calls</div>

        {uploading && uploadedFile && _.isEmpty(uploadInFlight) && (
          <InProgressCallRecordingItem
            uploadedFile={uploadedFile}
            uploading={uploading}
            progress={uploadProgress}
          />
        )}

        {failed && uploadedFile && (
          <ProfileGenerationFailed
            uploadedFile={uploadedFile}
            onDelete={() => {
              setFailed(false);
              setUploadedFile(null);
            }}
          />
        )}

        <div className={styles.callList}>
          {isEmpty && (
            <div className={styles.emptyMessage}>
              <div className={styles.text}>no calls yet</div>
              <span className={styles.subText}>
                upload a recorded sales call you want to assess.
              </span>
            </div>
          )}

          <InfiniteScroll
            className={styles.list}
            loading={uploadedItemsLoading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMore}
            loader={() => {
              return _.times(
                _.size(uploadedItems) === 0 || currentPage === 0 ? 3 : 1,
                (index) => <CallRecordingPlaceholder key={index} />,
              );
            }}
          >
            {isUploading && (
              <CallRecordingPlaceholder />
            )}

            {uploadedItems.map((item: UploadedCallItem) => (
              <CallRecordingItem
                key={item.recordingId}
                uploadState={item}
                onDelete={() => handleDelete(item)}
              />
            ))}
          </InfiniteScroll>
        </div>
      </Slider.Body>

      <Slider.Actions>
        {deleteState.show && (
          <DeleteConfirmationDialog
            item={deleteState}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
          />
        )}
        {!deleteState.show && (
          <Button variant="secondary" text="Close" onClick={handleClose} />
        )}
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface InProgressCallRecordingItemProps {
  uploadedFile: any;
  uploading: boolean;
  progress: number;
}

const InProgressCallRecordingItem: React.FC<InProgressCallRecordingItemProps> = ({ uploadedFile, uploading, progress }) => {
  return (
    <div className={styles.asset}>
      <Thumbnail
        media={null}
        defaultImage="/images/assets/default_asset.jpg"
        className={styles.icon}
      />
      <div className={styles.fileInfo}>
        <div className={styles.fileDetails}>
          <span className={styles.fileName}>{uploadedFile.name}</span>
          {uploading && (
            <p className={styles.description}>
              call upload in progress...
            </p>
          )}
        </div>
        {uploading && (
          <div className={styles.progressWrapper}>
            <ProgressBar now={progress} />
          </div>
        )}
      </div>
    </div>
  );
}

interface DeleteConfirmationDialogProps {
  item: any;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  item,
  onConfirm,
  onCancel,
}) => {

  const deleting = useSelector((state: any) => {
    return _.get(
      state.coaching,
      `callMeetings.deletion['${item?.file?.id}'].deleting`,
    ) as boolean;
  });

  return (
    <div>
      <div className={`${styles.deleteConfirmation} ${item.isClosing ? styles.closing : ''}`}>
        <div className={styles.message}>
          Are you sure you want to delete the call {" "}
          <span className={styles.name}>"{item.file?.name}"?</span>
        </div>

        <div className={styles.actions}>
          <Button
            variant="primary"
            icon="paperflite-delete"
            text={deleting ? "Deleting..." : "Delete"}
            onClick={onConfirm}
          ></Button>
          <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>
        </div>
      </div>
    </div>
  );
}; 

interface CallRecordingItemProps {
  uploadState: any;
  onDelete: () => void;
}

const CallRecordingItem: React.FC<CallRecordingItemProps> = ({ uploadState, onDelete }) => {

  const response = uploadState.response;

  const transcriptProcessingStatus = useMemo(() => {
    if (!uploadState.transcriptProcessing) return "pending";
    return uploadState.transcriptProcessing.status;
  }, [uploadState]);

  const evaluationProcessingStatus = useMemo(() => {
    if (!uploadState.evaluationProcessing) return "pending";
    return uploadState.evaluationProcessing.status;
  }, [uploadState]);

  const hasInsufficientData = useMemo(() => {
    const transcriptFailed = transcriptProcessingStatus === "failed";
    const evaluationFailed = evaluationProcessingStatus === "failed";
    const isFailed = uploadState.response?.status === "FAILED";
  
    return transcriptFailed || evaluationFailed || isFailed;
  }, [uploadState, transcriptProcessingStatus, evaluationProcessingStatus]);

  const inProgress = !hasInsufficientData && (!uploadState.evaluationProcessing ||
    evaluationProcessingStatus !== "completed");

  const isCompleted = evaluationProcessingStatus === "completed" && !hasInsufficientData;

  const callRecordingName = useMemo(() => {
    return _.get(response, "title");
  }, [response]);

  return (
    <div className={styles.listContainer}>
      <div className={styles.metadatas}>
        <div className={styles.metaInfo}>
          <span className={styles.metadata}>
            {response?.createdDate
              ? dateformat(response.createdDate, "dd mmm  hh:MM TT")
              : "—"}
          </span>
        </div>

        <div className={cx(styles.metaActions, {
            [styles.showOnHover]: !inProgress,
            [styles.show]: inProgress,
          })}>
          {inProgress && (
            <i className={cx("fa-solid fa-spinner fa-spin-pulse", styles.spinner)} />
          )}

          {!inProgress && (
            <>
              {isCompleted && (
                <>
                  <IconButton
                    className={styles.tickIcon}
                    icon="paperflite-tick"
                  />
                  <div className={styles.divider} />
                </>
              )}
              <IconButton
                className={styles.deleteIcon}
                icon="paperflite-delete"
                onClick={onDelete}
              />
            </>
          )}
        </div>
      </div>

      <div className={styles.heading}>{callRecordingName}</div>

      {inProgress && (
        <p className={styles.description}>call processing in progress...</p>
      )}

      {!inProgress && hasInsufficientData && (
        <div className={styles.warning}>
          <i className="paperflite-danger" />
          <span className={styles.warningMessage}>insufficient data</span>
        </div>
      )}

      <div className={styles.footer}>
        <Thumbnail
          className={styles.avatar}
          media={response?.icon}
          defaultImage="/images/assets/default_asset.jpg"
          width={32}
        />
        <span className={styles.name}>by {response?.uploadedBy}</span>
      </div>
    </div>
  );
};

const IconButton: React.FC<any> = ({ icon, title, onClick, disabled, className }) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  }
  return (
    <button disabled={disabled} onClick={handleClick} className={cx(styles.iconButton, className, {
      [styles.disabled]: disabled
    })}>
      <i className={icon}></i>
      <span className={styles.title}>{title}</span>
    </button>
  )
}

interface ProfileGenerationFailedProps {
  uploadedFile: any;
  onDelete: () => void; 
}

const ProfileGenerationFailed: React.FC<ProfileGenerationFailedProps> = ({ uploadedFile, onDelete }) => {
  return (
    <div className={styles.profileGenerationFailed}>
      {uploadedFile && (
        <div className={styles.icon}>
          <Thumbnail
            className={styles.icon}
            media={uploadedFile.icon}
            defaultImage="/images/assets/default_asset.jpg"
          />
          <i className="paperflite-danger" />
        </div>
      )}
      <div className={styles.details}>
      <div className={styles.info}>
        <div className={styles.name}>{uploadedFile.name}</div>
        <span className={styles.description}>
          upload failed — please try reuploading.
        </span>
      </div>

      <div className={styles.actionsContainer}>
        <button className={styles.deleteButton} onClick={onDelete}>
          <i className="paperflite-delete"></i>
        </button>
      </div>
      </div>

    </div>
  );
}

export const CallRecordingPlaceholder = () => {
  return (
    <div className={cx(styles.listContainer, styles.callListplaceholder)}>
      <div className={styles.metadatas}>
        <div className={styles.meta} />
      </div>

      <div className={styles.headerContainer} />

      <div className={styles.details}>
        <div className={styles.detailRow}>
          <div className={styles.block} />
        </div>
        <div className={styles.detailRow}>
          <div className={styles.block} />
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.avatar} />
        <div className={styles.block} />
      </div>
    </div>
  );
};